import clsx from "clsx";

import { Dialog, DialogContent } from "common/components/ui/Dialog";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LicensesStep } from "common/types";

import Success from "../Success";
import AccountsToCancel from "./AccountsToCancel";
import ManageSeats from "./ManageSeats";
import SeatPurchase from "./SeatPurchase";
import Summary from "./Summary";
import useLicensesState from "./useLicensesState";

import type { ReactNode } from "react";

interface LicensesProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  newSeatsCount?: number;
  accountIds?: string[];
}

export default function Licenses({
  isOpen,
  setIsOpen,
  newSeatsCount = 0,
  accountIds = null,
}: LicensesProps) {
  const isDesktop = useTwBreakpoint("lg");
  const {
    dispatch,
    assignedLicenseCount,
    shouldRemoveAccounts,
    resetState,
    state,
  } = useLicensesState(newSeatsCount);

  const {
    licenseCount,
    selectedPeriodUnit,
    accountsToCancel,
    currentStep,
    paymentSource,
  } = state;

  const steps: Record<LicensesStep, ReactNode> = {
    seatPurchase: (
      <SeatPurchase
        newSeatsCount={newSeatsCount}
        onNext={() => dispatch({ type: "NEXT_STEP", payload: "summary" })}
      />
    ),
    manageSeats: (
      <ManageSeats
        selectedPeriodUnit={selectedPeriodUnit}
        setSelectedPeriodUnit={(periodUnit) =>
          dispatch({ type: "SET_PERIOD_UNIT", payload: periodUnit })
        }
        licenseCount={licenseCount}
        setLicenseCount={(newCount) => {
          dispatch({ type: "SET_LICENSE_COUNT", payload: newCount });
        }}
        goToNextStep={() =>
          dispatch({
            type: "NEXT_STEP",
            payload: shouldRemoveAccounts ? "accountsToCancel" : "summary",
          })
        }
      />
    ),
    accountsToCancel: (
      <AccountsToCancel
        goBack={() => dispatch({ type: "NEXT_STEP", payload: "manageSeats" })}
        goToNextStep={() => dispatch({ type: "NEXT_STEP", payload: "summary" })}
        accountsToCancel={accountsToCancel}
        setAccountsToCancel={(accounts) =>
          dispatch({ type: "SET_ACCOUNTS_TO_CANCEL", payload: accounts })
        }
        seatsToCancel={assignedLicenseCount - licenseCount}
      />
    ),
    summary: (
      <Summary
        licenseCount={licenseCount}
        selectedPeriodUnit={selectedPeriodUnit}
        goBack={() =>
          dispatch({
            type: "NEXT_STEP",
            payload: shouldRemoveAccounts ? "accountsToCancel" : "manageSeats",
          })
        }
        goToSuccess={() => dispatch({ type: "NEXT_STEP", payload: "success" })}
        accountsToCancel={accountsToCancel}
        accountIds={accountIds}
        paymentSource={paymentSource}
        setSelectedPaymentSource={(newPaymentSource) =>
          dispatch({
            type: "SET_PAYMENT_SOURCE",
            payload: newPaymentSource,
          })
        }
      />
    ),
    success: <Success />,
  };

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;

  const stepsWithPadding: LicensesStep[] = [
    "manageSeats",
    "accountsToCancel",
    "summary",
  ];

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={resetState}>
      <Content
        className={clsx(
          "transition-none lg:w-[448px]",
          stepsWithPadding.includes(currentStep) && "max-lg:pb-[72px]",
        )}
      >
        {steps[currentStep]}
      </Content>
    </Component>
  );
}
